import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const SITE_NAME = "PullOver";
const SITE_URL = "https://pullover.app";
const RECIPIENT = "rep@jlrwaterloo.com";
const NEW_EMAIL = "rep.new@jlrwaterloo.com";
const ACTION_URL = "https://pullover.app/auth/confirm?token=demo";
const TOKEN = "284917";

// Mirror of the auth email template styles (kept in sync with
// supabase/functions/_shared/email-templates/*.tsx)
const main: React.CSSProperties = {
  backgroundColor: "#ffffff",
  fontFamily: "Montserrat, Helvetica, Arial, sans-serif",
  color: "#0f172a",
};
const container: React.CSSProperties = { padding: "32px 28px", maxWidth: 560, margin: "0 auto" };
const h1: React.CSSProperties = {
  fontSize: 24,
  fontWeight: "bold",
  color: "#0f172a",
  margin: "0 0 20px",
  letterSpacing: "-0.01em",
};
const text: React.CSSProperties = {
  fontSize: 15,
  color: "#475569",
  lineHeight: 1.6,
  margin: "0 0 24px",
};
const link: React.CSSProperties = { color: "#dc2626", textDecoration: "underline" };
const button: React.CSSProperties = {
  display: "inline-block",
  backgroundColor: "#dc2626",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: "bold",
  borderRadius: 8,
  padding: "12px 22px",
  textDecoration: "none",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const code: React.CSSProperties = {
  fontFamily: "Menlo, Courier, monospace",
  fontSize: 28,
  fontWeight: "bold",
  color: "#dc2626",
  letterSpacing: "0.2em",
  margin: "0 0 30px",
};
const footer: React.CSSProperties = { fontSize: 12, color: "#94a3b8", margin: "32px 0 0" };

type TemplateKey =
  | "signup"
  | "invite"
  | "magic-link"
  | "recovery"
  | "email-change"
  | "reauthentication";

type TemplateMeta = {
  key: TemplateKey;
  label: string;
  subject: string;
  preview: string;
  body: JSX.Element;
};

const templates: TemplateMeta[] = [
  {
    key: "signup",
    label: "Confirm signup",
    subject: `Confirm your email for ${SITE_NAME}`,
    preview: `Confirm your email for ${SITE_NAME}`,
    body: (
      <>
        <h1 style={h1}>Confirm your email</h1>
        <p style={text}>
          Thanks for signing up for{" "}
          <a href={SITE_URL} style={link}>
            <strong>{SITE_NAME}</strong>
          </a>
          !
        </p>
        <p style={text}>
          Please confirm your email address (
          <a href={`mailto:${RECIPIENT}`} style={link}>
            {RECIPIENT}
          </a>
          ) by clicking the button below:
        </p>
        <a href={ACTION_URL} style={button}>
          Verify Email
        </a>
        <p style={footer}>
          If you didn&apos;t create an account, you can safely ignore this email.
        </p>
      </>
    ),
  },
  {
    key: "invite",
    label: "Invite to workspace",
    subject: `You've been invited to join ${SITE_NAME}`,
    preview: `You've been invited to join ${SITE_NAME}`,
    body: (
      <>
        <h1 style={h1}>You&apos;ve been invited</h1>
        <p style={text}>
          You&apos;ve been invited to join{" "}
          <a href={SITE_URL} style={link}>
            <strong>{SITE_NAME}</strong>
          </a>
          . Click the button below to accept the invitation and create your account.
        </p>
        <a href={ACTION_URL} style={button}>
          Accept Invitation
        </a>
        <p style={footer}>
          If you weren&apos;t expecting this invitation, you can safely ignore this email.
        </p>
      </>
    ),
  },
  {
    key: "magic-link",
    label: "Magic link login",
    subject: `Your login link for ${SITE_NAME}`,
    preview: `Your login link for ${SITE_NAME}`,
    body: (
      <>
        <h1 style={h1}>Your login link</h1>
        <p style={text}>
          Click the button below to log in to {SITE_NAME}. This link will expire shortly.
        </p>
        <a href={ACTION_URL} style={button}>
          Log In
        </a>
        <p style={footer}>
          If you didn&apos;t request this link, you can safely ignore this email.
        </p>
      </>
    ),
  },
  {
    key: "recovery",
    label: "Password reset",
    subject: `Reset your password for ${SITE_NAME}`,
    preview: `Reset your password for ${SITE_NAME}`,
    body: (
      <>
        <h1 style={h1}>Reset your password</h1>
        <p style={text}>
          We received a request to reset your password for {SITE_NAME}. Click the button below to
          choose a new password.
        </p>
        <a href={ACTION_URL} style={button}>
          Reset Password
        </a>
        <p style={footer}>
          If you didn&apos;t request a password reset, you can safely ignore this email. Your
          password will not be changed.
        </p>
      </>
    ),
  },
  {
    key: "email-change",
    label: "Email change",
    subject: `Confirm your email change for ${SITE_NAME}`,
    preview: `Confirm your email change for ${SITE_NAME}`,
    body: (
      <>
        <h1 style={h1}>Confirm your email change</h1>
        <p style={text}>
          You requested to change your email address for {SITE_NAME} from{" "}
          <a href={`mailto:${RECIPIENT}`} style={link}>
            {RECIPIENT}
          </a>{" "}
          to{" "}
          <a href={`mailto:${NEW_EMAIL}`} style={link}>
            {NEW_EMAIL}
          </a>
          .
        </p>
        <p style={text}>Click the button below to confirm this change:</p>
        <a href={ACTION_URL} style={button}>
          Confirm Email Change
        </a>
        <p style={footer}>
          If you didn&apos;t request this change, please secure your account immediately.
        </p>
      </>
    ),
  },
  {
    key: "reauthentication",
    label: "Reauthentication code",
    subject: "Your verification code",
    preview: "Your verification code",
    body: (
      <>
        <h1 style={h1}>Confirm reauthentication</h1>
        <p style={text}>Use the code below to confirm your identity:</p>
        <p style={code}>{TOKEN}</p>
        <p style={footer}>
          This code will expire shortly. If you didn&apos;t request this, you can safely ignore
          this email.
        </p>
      </>
    ),
  },
];

export default function EmailPreview() {
  const [active, setActive] = useState<TemplateKey>("signup");
  const current = useMemo(() => templates.find((t) => t.key === active)!, [active]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Link>
            </Button>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Auth email previews</h1>
              <p className="text-xs text-muted-foreground">
                Sender: notify@pullover.app · Branding mirror of the live templates
              </p>
            </div>
          </div>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            6 templates
          </span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-[240px_1fr] gap-8">
        <nav className="space-y-1">
          {templates.map((t) => {
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <main>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-muted/40 text-xs space-y-1">
              <div>
                <span className="text-muted-foreground">From: </span>
                <span className="font-medium">PullOver &lt;notify@pullover.app&gt;</span>
              </div>
              <div>
                <span className="text-muted-foreground">To: </span>
                <span className="font-medium">{RECIPIENT}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Subject: </span>
                <span className="font-semibold">{current.subject}</span>
              </div>
              <div className="text-muted-foreground italic">Preview: {current.preview}</div>
            </div>

            <div style={main}>
              <div style={container}>{current.body}</div>
              <div
                style={{
                  borderTop: "1px solid #e2e8f0",
                  padding: "16px 28px",
                  fontSize: 11,
                  color: "#94a3b8",
                  textAlign: "center" as const,
                }}
              >
                Sent by {SITE_NAME} · This is a preview — system-managed footer (e.g.
                unsubscribe) is appended on real sends.
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Source of truth: <code>supabase/functions/_shared/email-templates/{current.key}.tsx</code>
          </p>
        </main>
      </div>
    </div>
  );
}